import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, ArrowLeft, Building2, Search } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';

import { Answer } from '@/components/common/Answer';
import { AsOfStrip } from '@/components/common/AsOfStrip';
import { ExplainerCard } from '@/components/common/ExplainerCard';
import { ExportButton } from '@/components/common/ExportButton';
import { FieldGuide } from '@/components/common/FieldGuide';
import { ErrorMessage, SkeletonLoader } from '@/components/common/StateViews';
import { ApiError } from '@/lib/api';
import { fetchCadeParty } from '@/services/cade';
import { AttributedFineRow } from '@/pages/cade/AttributedFineRow';
import { Stat } from '@/pages/cade/Stat';
import type {
  CadeClusterSibling,
  CadePartyCnpjLink,
  CadePartyAppearance,
  CadePartyAttribution,
  CadePartyIdentity,
  CadePartyRecord,
  CadePartySpelling,
} from '@/services/types';

/**
 * One party as the CADE session archive recorded the NAME.
 *
 * Reached by clicking a party name, and listed at `/cade/parties`. Deliberately
 * NOT a company profile: `name_canonical` is 16,423 distinct values over 16,423
 * rows, so the same firm sits under its misspellings and former names as
 * separate parties. The page says that first, above the counts, rather than
 * under them.
 *
 * **The outcome panel is the point of the screen.** CADE records a ROLE for a
 * party on a decision and nothing about how the matter resolved for them, so
 * attributing a decision's outcome to a party named on it is a fallacy of
 * composition -- and on a page that may name a private individual, it would
 * state wrongdoing that is not in the record. It is refused, at CORPUS scope,
 * and the decision list below carries the same caveat where a reader will meet
 * the codes.
 *
 * Order, since 2026-09-14: the one-line "a name, not a company" caveat stays
 * at the top; then THIS record -- its window, roles, appearances and
 * attributions; the corpus-level identity panel (3,209 / 4 / 13,214) sits
 * below as context. On the directory the caveat precedes the names by design;
 * here the h1 is the name and the subtitle already says it is not a company,
 * so a reader arriving for one client should meet that client's record before
 * the corpus's arithmetic.
 */
export function CadePartyProfilePage() {
  const { slug = '' } = useParams();
  const { t, i18n } = useTranslation(['cade', 'common']);
  const number = new Intl.NumberFormat(i18n.language);
  const currency = new Intl.NumberFormat(i18n.language, {
    style: 'currency',
    currency: 'BRL',
  });

  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['cade', 'party', slug],
    queryFn: () => fetchCadeParty(slug),
    // A 404 means this slug names nobody. Retrying it turns a correct answer
    // into a slow spinner.
    retry: (count, err) => !(err instanceof ApiError && err.status === 404) && count < 1,
  });

  if (isPending) {
    return (
      <div className="space-y-4 p-6">
        <BackLink />
        <SkeletonLoader rows={8} />
      </div>
    );
  }

  if (error) {
    const notFound = error instanceof ApiError && error.status === 404;
    return (
      <div className="space-y-4 p-6">
        <BackLink />
        <ErrorMessage
          message={notFound ? t('cade:parties.notFound') : error.message}
          onRetry={notFound ? undefined : () => void refetch()}
        />
      </div>
    );
  }

  const record = data.record.value;
  const name = record?.name ?? slug;

  return (
    <div className="space-y-4 p-6">
      <BackLink />

      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1">
          <h1 className="text-lg font-semibold text-foreground">{name}</h1>
          <p className="text-sm text-muted-foreground">{t('cade:parties.profileSubtitle')}</p>
          {/* The record's window, under the name: the archive years of the
              sessions naming it, from the same rows the list below shows. */}
          {record && record.first_year !== null && record.last_year !== null ? (
            <p className="text-xs text-muted-foreground" data-record-window>
              {t('cade:parties.window', {
                window:
                  record.first_year === record.last_year
                    ? String(record.first_year)
                    : t('common:asOf.yearWindow', {
                        first: record.first_year,
                        last: record.last_year,
                      }),
                count: record.decisions,
              })}
            </p>
          ) : null}
        </div>
        <ExportButton path={`/api/v1/cade/export/parties/${encodeURIComponent(slug)}`} />
      </header>

      {/* The one-line caveat stays FIRST: a reader who meets "14 decisions"
          before "this is a name, not a company" has already formed the wrong
          reading. The corpus arithmetic behind it moved below the record. */}
      {data.identity.value ? (
        <p className="flex items-start gap-2 rounded-md border border-amber-500/40 bg-amber-500/5 p-3 text-xs text-foreground">
          <AlertTriangle
            className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400"
            aria-hidden
          />
          <span>
            {data.identity.value.resolved
              ? t('cade:cases.parties.resolved', { count: data.identity.value.merged })
              : t('cade:cases.parties.unresolved')}
          </span>
        </p>
      ) : null}

      {/* A clock or a date the parse read as a name. Said before the rows,
          which are kept because they exist: the real party's name was not
          captured on them, and dropping the rows would hide that. */}
      {record?.is_artefact ? (
        <p
          className="flex items-start gap-2 rounded-md border border-amber-500/40 bg-amber-500/5 p-3 text-xs text-foreground"
          role="note"
          data-artefact-notice
        >
          <AlertTriangle
            className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400"
            aria-hidden
          />
          <span>{t('cade:parties.artefactNotice')}</span>
        </p>
      ) : null}

      <ExplainerCard i18nKey="cade:parties.explainer" />
      <AsOfStrip asOf={data.as_of} />

      <section className="rounded-lg border bg-card p-4">
        <h2 className="mb-3 text-sm font-semibold text-foreground">
          {t('cade:parties.record.title')}
        </h2>
        <Answer answer={data.record} scope="record" help={t('cade:parties.record.help')}>
          {(value) => (
            <RecordPanel record={value} cnpjFormatted={data.cnpj_link?.cnpj_formatted ?? null} />
          )}
        </Answer>
        <div className="mt-3">
          <FieldGuide i18nKey="cade:parties.guide.record" />
        </div>
      </section>

      {/* CORPUS scope, deliberately, and the only panel on this detail page that
          uses it. No party in this corpus has a recorded outcome, so
          "unavailable for this agency" is literally accurate. `record` scope
          would say "not present here", implying other parties' outcomes ARE
          recorded and this one's are missing -- which would send a reader
          looking, and invite them to read the codes below as this party's own. */}
      <section className="rounded-lg border bg-card p-4">
        <h2 className="mb-2 text-sm font-semibold text-foreground">
          {t('cade:parties.outcome.title')}
        </h2>
        <Answer
          answer={data.outcome}
          scope="corpus"
          help={t('cade:parties.outcome.help')}
          elsewhere={t('cade:parties.outcome.elsewhere')}
        >
          {() => null}
        </Answer>
      </section>

      {data.cnpj_link ? <CnpjLinkPanel link={data.cnpj_link} /> : null}
      {record ? <SpellingsPanel record={record} /> : null}
      {record && record.cluster_siblings.length > 0 ? <ClusterPanel record={record} /> : null}

      <AppearanceList items={data.appearances} />

      {/* RECORD scope here, unlike the outcome panel: the corpus supports
          attributed fines perfectly well -- 435 of them -- and this name simply
          carries none. */}
      <section className="space-y-3 rounded-lg border bg-card p-4">
        <h2 className="text-sm font-semibold text-foreground">
          {t('cade:parties.attributions.title')}
        </h2>
        <Answer
          answer={data.attributions}
          scope="record"
          help={t('cade:parties.attributions.help')}
        >
          {(rows) => <AttributionList rows={rows} currency={currency} />}
        </Answer>
      </section>

      <p className="text-xs text-muted-foreground">
        {t('cade:parties.appearances.ordering', { total: number.format(data.appearances.length) })}
      </p>

      {/* The corpus-level identity panel, as context AFTER the record. Reused
          from `/cade/cases`, never re-authored. */}
      <section className="rounded-lg border bg-card p-4">
        <h2 className="mb-3 text-sm font-semibold text-foreground">
          {t('cade:parties.identity.title')}
        </h2>
        <Answer answer={data.identity} scope="corpus" help={t('cade:parties.identity.help')}>
          {(identity: CadePartyIdentity) => <IdentityPanel identity={identity} name={name} />}
        </Answer>
      </section>
    </div>
  );
}

function BackLink() {
  const { t } = useTranslation(['cade']);
  return (
    <Link
      to="/cade/parties"
      className="inline-flex items-center gap-1 text-xs text-primary underline-offset-4 hover:underline"
    >
      <ArrowLeft className="h-3 w-3" aria-hidden />
      {t('cade:parties.back')}
    </Link>
  );
}

function IdentityPanel({ identity, name }: { identity: CadePartyIdentity; name: string }) {
  const { t, i18n } = useTranslation(['cade']);
  const number = new Intl.NumberFormat(i18n.language);

  // The caveat itself is rendered ONCE, at the top of the page; this panel
  // carries the arithmetic behind it and the two searches.
  return (
    <div className="space-y-2 text-xs">
      <dl className="grid grid-cols-2 gap-2 sm:grid-cols-3">
        <div>
          <dt className="text-muted-foreground">{t('cade:cases.parties.records')}</dt>
          <dd className="tabular-nums text-foreground">{number.format(identity.linked)}</dd>
        </div>
        <div>
          <dt className="text-muted-foreground">{t('cade:cases.parties.withCnpj')}</dt>
          <dd className="tabular-nums text-foreground">{number.format(identity.with_cnpj)}</dd>
        </div>
        {/* The other population in the same table, stated beside the one this
            page belongs to: these rows arrive from CADE's published register
            and appear on no tribunal decision, so no page here can reach
            them. */}
        <div>
          <dt className="text-muted-foreground">{t('cade:cases.parties.registryOnly')}</dt>
          <dd className="tabular-nums text-foreground">
            {number.format(identity.registry_only)}
          </dd>
        </div>
      </dl>
      {identity.cnpj_cluster_conflicts ? (
        <p className="text-muted-foreground">
          {t('cade:cases.parties.cnpjConflicts', { count: identity.cnpj_cluster_conflicts })}
        </p>
      ) : null}
      {/* The wider net, beside the disclosure that motivates it. This page shows
          one spelling; the case search finds every case whose party names match
          the string, which is the only way to reach the variants. */}
      <div className="flex flex-wrap gap-4">
        <Link
          to={`/cade/cases?q=${encodeURIComponent(name)}`}
          className="inline-flex items-center gap-1 text-primary underline-offset-4 hover:underline"
        >
          <Search className="h-3 w-3" aria-hidden />
          {t('cade:parties.wideNet')}
        </Link>
        {/* The whole ata item names the parties and their counsel as
            minuted, so a search over it finds mentions the party links may
            have missed -- a header naming this firm on an item that links a
            different spelling. */}
        <Link
          to={`/cade/decisions?q=${encodeURIComponent(name)}&scope=item`}
          className="inline-flex items-center gap-1 text-primary underline-offset-4 hover:underline"
        >
          <Search className="h-3 w-3" aria-hidden />
          {t('cade:parties.searchInAta')}
        </Link>
      </div>
    </div>
  );
}

/**
 * The CNPJ ETL 66 linked to this NAME on evidence, the evidence itself, and the way into the
 * company check -- which gathers every spelling linked to the same root. Shown only when a link
 * exists: an unlinked name is not a company without a CNPJ, and the identity panel says so.
 */
function CnpjLinkPanel({ link }: { link: CadePartyCnpjLink }) {
  const { t } = useTranslation(['cade']);
  return (
    <section className="space-y-2 rounded-lg border bg-card p-4 text-xs" data-party-cnpj>
      <h2 className="text-sm font-semibold text-foreground">{t('cade:parties.cnpjLink.title')}</h2>
      <p className="text-muted-foreground">{t('cade:parties.cnpjLink.help')}</p>
      <dl className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Stat label="CNPJ" value={link.cnpj_formatted} />
        <Stat emphasis={false} label={t('cade:parties.cnpjLink.legalName')} value={link.legal_name ?? '—'} />
        <Stat emphasis={false} label={t('cade:parties.cnpjLink.cnae')} value={link.cnae_main ?? '—'} />
        <Stat
          emphasis={false}
          label={t('cade:parties.cnpjLink.method')}
          value={t(`cade:company.evidence.method.${link.method}`, { defaultValue: link.method })}
        />
      </dl>
      {link.quote ? (
        <blockquote className="whitespace-pre-line border-l-2 pl-3 text-muted-foreground">
          <span className="sr-only">{t('cade:parties.cnpjLink.quote')}: </span>
          {link.quote}
          {link.nup ? (
            <span className="mt-1 block not-italic text-foreground">
              {t('cade:parties.cnpjLink.process')}: {link.nup}
            </span>
          ) : null}
        </blockquote>
      ) : null}
      <Link
        to={`/cade/company/${link.root}`}
        className="inline-flex items-center gap-1 text-primary underline-offset-4 hover:underline"
      >
        <Building2 className="h-3 w-3" aria-hidden />
        {t('cade:parties.cnpjLink.company')}
      </Link>
    </section>
  );
}

function RecordPanel({
  record,
  cnpjFormatted,
}: {
  record: CadePartyRecord;
  cnpjFormatted: string | null;
}) {
  const { t, i18n } = useTranslation(['cade']);
  const number = new Intl.NumberFormat(i18n.language);

  return (
    <div className="space-y-3 text-xs">
      <dl className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Stat
          label={t('cade:parties.stats.decisions')}
          value={`${number.format(record.decisions)} / ${number.format(
            record.corpus_linked_decisions,
          )}`}
        />
        <Stat
          label={t('cade:parties.stats.finalDecisions')}
          value={number.format(record.final_decisions)}
        />
        <Stat emphasis={false} label={t('cade:parties.stats.cases')} value={number.format(record.cases)} />
        <Stat emphasis={false} label={t('cade:parties.stats.cnpj')} value={cnpjFormatted ?? '—'} />
      </dl>

      <div>
        <h3 className="mb-1 font-medium text-foreground">{t('cade:parties.roles.title')}</h3>
        <ul className="space-y-1">
          {record.roles.map((role) => (
            <li key={role.role} className="flex items-baseline justify-between gap-3">
              <span className="text-muted-foreground">
                {t(`cade:parties.roles.${role.role}`, { defaultValue: role.role })}
              </span>
              <span className="tabular-nums text-foreground">{number.format(role.decisions)}</span>
            </li>
          ))}
        </ul>
        {record.multi_role ? (
          <p className="mt-1 text-muted-foreground">{t('cade:parties.roles.multiRole')}</p>
        ) : null}
      </div>
    </div>
  );
}


function SpellingsPanel({ record }: { record: CadePartyRecord }) {
  const { t } = useTranslation(['cade']);
  return (
    <section className="space-y-2 rounded-lg border bg-card p-4 text-xs">
      <h2 className="text-sm font-semibold text-foreground">
        {t('cade:parties.spellings.title')}
      </h2>
      <p className="text-muted-foreground">{t('cade:parties.spellings.help')}</p>
      {record.spellings.length === 0 ? (
        <p className="text-muted-foreground">{t('cade:parties.spellings.none')}</p>
      ) : (
        <ul className="space-y-1">
          {record.spellings.map((spelling: CadePartySpelling, index) => (
            <li key={index} className="flex flex-wrap items-baseline justify-between gap-2">
              <span className="text-foreground">{spelling.raw_name}</span>
              <span className="shrink-0 text-muted-foreground">
                {t(`cade:parties.spellings.methods.${spelling.match_method}`, {
                  defaultValue: spelling.match_method,
                })}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function ClusterPanel({ record }: { record: CadePartyRecord }) {
  const { t } = useTranslation(['cade']);
  return (
    <section className="space-y-2 rounded-lg border bg-card p-4 text-xs">
      <h2 className="text-sm font-semibold text-foreground">{t('cade:parties.cluster.title')}</h2>
      <p className="text-muted-foreground">{t('cade:parties.cluster.help')}</p>
      <ul className="space-y-1">
        {record.cluster_siblings.map((sibling: CadeClusterSibling) => (
          <li key={sibling.slug} className="flex flex-wrap items-baseline justify-between gap-2">
            <Link
              to={`/cade/parties/${sibling.slug}`}
              className="text-primary underline-offset-4 hover:underline"
            >
              {sibling.name}
            </Link>
            {sibling.match_method ? (
              <span className="shrink-0 text-muted-foreground">
                {t(`cade:parties.spellings.methods.${sibling.match_method}`, {
                  defaultValue: sibling.match_method,
                })}
              </span>
            ) : null}
          </li>
        ))}
      </ul>
      {/* Never a count. The grouping folds in a row naming two companies and
          leaves known spelling variants out, so it may be followed and must not
          be summed. */}
      <p className="text-muted-foreground">{t('cade:parties.cluster.caveat')}</p>
    </section>
  );
}

function AppearanceList({ items }: { items: CadePartyAppearance[] }) {
  const { t, i18n } = useTranslation(['cade']);
  const number = new Intl.NumberFormat(i18n.language);

  return (
    <section className="space-y-3 rounded-lg border bg-card p-4">
      <h2 className="text-sm font-semibold text-foreground">
        {t('cade:parties.appearances.title', { total: number.format(items.length) })}
      </h2>
      {/* The caveat where the reader meets the codes, not only in the refusal
          panel above. Someone scanning this list is exactly the person about to
          read an outcome as this party's own. */}
      <p className="text-xs text-muted-foreground">
        {t('cade:parties.appearances.outcomeIsTheDecisions')}
      </p>
      {items.length === 0 ? (
        <p className="text-xs text-muted-foreground">{t('cade:parties.appearances.none')}</p>
      ) : (
        <ul
          className="max-h-[60vh] space-y-1.5 overflow-auto rounded-lg border p-2 text-xs"
          role="region"
          aria-label={t('cade:parties.appearances.listLabel')}
        >
          {items.map((item, index) => (
            // The index: one case can be adjudicated in several sessions under
            // the same item number, so (case, item) is not a key. The list is
            // server-ordered and never reordered client-side.
            <li
              key={index}
              className="flex flex-wrap items-baseline justify-between gap-2 rounded border p-2"
            >
              <span className="space-x-2">
                <Link
                  to={`/cade/c/${item.case_key}`}
                  className="font-medium tabular-nums text-primary underline-offset-4 hover:underline"
                >
                  {item.case_number}
                </Link>
                {/* The decision itself -- its votes as minuted, its fines,
                    its text -- one click away from the appearance. */}
                <Link
                  to={`/cade/votes/d/${encodeURIComponent(item.decision_key)}`}
                  className="text-primary underline-offset-4 hover:underline"
                >
                  {t('cade:case.openDecision')}
                </Link>
                <span className="text-muted-foreground">
                  {item.role
                    ? t(`cade:parties.roles.${item.role}`, { defaultValue: item.role })
                    : '—'}
                </span>
                {/* What the case page and the conselheiro page say about the
                    same row: its type, its agenda item, whether it is the
                    case's last observed decision, and the session's archive
                    year beside the prose date. */}
                {item.case_type ? (
                  <span className="text-muted-foreground">{item.case_type}</span>
                ) : null}
                {item.item_number ? (
                  <span className="text-muted-foreground">
                    {t('cade:case.item', { item: item.item_number })}
                  </span>
                ) : null}
                {item.is_final_observed ? (
                  <span className="rounded border border-primary/40 bg-primary/10 px-1.5 py-0.5 text-primary">
                    {t('cade:case.final')}
                  </span>
                ) : null}
                {item.session_year !== null ? (
                  <span className="tabular-nums text-muted-foreground">{item.session_year}</span>
                ) : null}
                {item.session_date_raw ? (
                  <span className="text-muted-foreground">{item.session_date_raw}</span>
                ) : null}
              </span>
              <span className="shrink-0 text-muted-foreground">
                {item.outcome_codes.length === 0
                  ? t('cade:overview.outcomes.UNCLASSIFIED')
                  : item.outcome_codes
                      .map((code) => t(`cade:overview.outcomes.${code}`, { defaultValue: code }))
                      .join(', ')}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function AttributionList({
  rows,
  currency,
}: {
  rows: CadePartyAttribution[];
  currency: Intl.NumberFormat;
}) {
  const { t } = useTranslation(['cade']);
  return (
    <div className="space-y-2 text-xs">
      {/* Stated where the amounts are, not only in the notes. This is the list
          someone would otherwise add up. */}
      <p className="text-muted-foreground">{t('cade:parties.attributions.neverTotalled')}</p>
      <ul className="space-y-1.5">
        {rows.map((row, index) => (
          <li key={index} className="rounded border p-2">
            {/* The shared row, so this page badges the verified quote and the
                regex-produced attribution exactly as the case and sanctions
                pages do on the same rows. It had shown the extractor and the
                span and neither badge. The case is the subject here. */}
            <AttributedFineRow
              fine={row}
              currency={currency}
              subject={
                <Link
                  to={`/cade/c/${row.case_key}`}
                  className="font-medium tabular-nums text-primary underline-offset-4 hover:underline"
                >
                  {row.case_number}
                </Link>
              }
            />
          </li>
        ))}
      </ul>
    </div>
  );
}
